import { useCallback, useMemo } from 'react';

import AutocompleteWithChips from 'components/Basic/AutocompleteWithChips/AutocompleteWithChips';
import { LookupDateRangeQueryItemDto } from 'types/CodegenLookupTypes';
import { SelectedItemDto } from 'types/CodegenReportTypes';
import { usePostLookupPitchers } from 'hooks/CodegenLookupHooks';
import { useTranslation } from 'react-i18next';

type Props = {
  selectedPitchers: SelectedItemDto[] | undefined;
  onChange: (pitchers: SelectedItemDto[]) => void;
  dateRanges: LookupDateRangeQueryItemDto[] | undefined;
  levelId: string | undefined;
  teamId: string | undefined;
};

const SelectPitchers = ({ selectedPitchers, onChange, dateRanges, levelId, teamId }: Props) => {
  const { t } = useTranslation();
  const { data, isLoading } = usePostLookupPitchers(dateRanges, levelId, teamId);

  const allPitcherIds = useMemo(() => data?.map((datum) => datum.id) ?? [], [data]);
  const getPitcherName = useCallback(
    (pitcherId: string) => new Map(data?.map((datum) => [datum.id, datum.name])).get(pitcherId) ?? (t('Nonexistent pitcher') as string),
    [data]
  );

  return (
    <AutocompleteWithChips
      label={t('Pitchers')}
      options={allPitcherIds}
      selected={selectedPitchers?.map((pitcher) => pitcher.id) ?? []}
      setSelected={(selectedIds) =>
        onChange(selectedIds.map((id) => data?.find((datum) => datum.id === id)).filter(Boolean) as SelectedItemDto[])
      }
      getOptionLabel={(option) => (isLoading ? t('Loading pitchers...') : getPitcherName(option))}
      noOptionsText={isLoading ? t('Loading pitchers...') : t('No pitchers found')}
      disabled={!dateRanges || !levelId || !teamId}
    />
  );
};

export default SelectPitchers;
